/**
 * Dialog zu einer Aktion am Antrag (ADR 011): fragt genau die Felder ab, die die Aktion setzt,
 * dazu den Kommentar für die Antragshistorie – aufgebaut wie der Dialog am Nachweis
 * (`EvidenceUebergangDialog`).
 *
 * Welche Felder das sind, welche davon Pflicht und was sie vorschlagen, sagt der Server je Aktion
 * (`PermitProcedure.actions`); hier steht nur, wie ein Feld aussieht. Vorbelegt wird, was am
 * Antrag schon steht, sonst das heutige Datum, wo der Server es setzt, sonst der Vorschlag
 * (`suggest_after`, etwa die Vollständigkeitsprüfung einen Monat nach dem Eingang). Ein
 * vorgeschlagenes Feld läuft mit seinem Bezugsfeld mit, bis man es selbst ändert.
 *
 * Dokumente kommen zu den vorhandenen hinzu; der Dialog zeigt deshalb nur die neuen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { addDays, addMonths, format, parseISO } from "date-fns";
import DialogTemplate from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import Dropdown from "../../../common/inputs/Dropdown";
import InputFieldDate from "../../../common/inputs/InputFieldDate";
import InputFieldMultiline from "../../../common/inputs/InputFieldMultiline";
import DokumentListe from "../../../common/inputs/DokumentListe";
import { permitsApi } from "../../../../services/api/permits";
import {
  LookupOption,
  PermitProcedure,
  ProcedureAction,
  ProcedureActionField,
  ProcedureDocument,
  ProcedureStatus,
  ProcedureTransition,
} from "../../../../types";

interface AntragUebergangDialogProps {
  procedure: PermitProcedure | null;
  /** Die gewählte Aktion; `null` schließt den Dialog */
  action: ProcedureAction | null;
  onClose: () => void;
  /** Führt die Aktion aus; `false`, wenn der Server sie abgelehnt hat (Dialog bleibt offen) */
  onSubmit: (data: ProcedureTransition) => Promise<boolean>;
}

type Value = string | ProcedureDocument[];
type Values = Record<string, Value>;

const today = (): string => format(new Date(), "yyyy-MM-dd");

/** Wert eines Feldes am Antrag, sofern er dort unter demselben Namen steht */
const storedValue = (procedure: PermitProcedure, name: string): string =>
  ((procedure as unknown as Record<string, unknown>)[name] as
    | string
    | null
    | undefined) ?? "";

/** Datum plus Vorschlag; ohne Bezugsdatum kein Vorschlag */
const suggestion = (
  field: ProcedureActionField,
  base: string,
): string | null => {
  const offset = field.suggest_after;
  if (!offset || !base) return null;
  const date = addDays(addMonths(parseISO(base), offset.months), offset.days);
  return format(date, "yyyy-MM-dd");
};

/** Bezugsdatum eines Vorschlags: aus dem Dialog, wenn er es abfragt, sonst vom Antrag */
const baseOf = (
  procedure: PermitProcedure,
  action: ProcedureAction,
  values: Values,
  field: ProcedureActionField,
): string => {
  const base = field.suggest_after?.base;
  if (!base) return "";
  // Die Fristverlängerung rechnet vom bisherigen Wert desselben Feldes.
  if (base === field.name) return storedValue(procedure, base);
  return action.fields.some((f) => f.name === base)
    ? ((values[base] as string | undefined) ?? "")
    : storedValue(procedure, base);
};

const initialValues = (
  procedure: PermitProcedure,
  action: ProcedureAction,
): Values => {
  const values: Values = {};
  action.fields.forEach((field) => {
    if (field.kind === "documents") {
      values[field.name] = [];
      return;
    }
    const stored =
      field.fresh || field.suggest_after?.base === field.name
        ? ""
        : storedValue(procedure, field.name);
    values[field.name] = stored || (field.today_default ? today() : "");
  });
  // Vorschläge danach: Ihr Bezugsfeld muss im Dialog schon feststehen.
  action.fields.forEach((field) => {
    if (values[field.name] || !field.suggest_after) return;
    values[field.name] =
      suggestion(field, baseOf(procedure, action, values, field)) ?? "";
  });
  return values;
};

const isEmpty = (value: Value | undefined): boolean =>
  Array.isArray(value)
    ? value.every((document) => !document.url.trim())
    : !value?.trim();

const AntragUebergangDialog: React.FC<AntragUebergangDialogProps> = ({
  procedure,
  action,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<Values>({});
  const [comment, setComment] = useState("");
  const [target, setTarget] = useState<ProcedureStatus | "">("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [decisions, setDecisions] = useState<LookupOption[]>([]);

  useEffect(() => {
    if (!procedure || !action) return;
    setValues(initialValues(procedure, action));
    setComment("");
    setTarget(action.target_options[0] ?? "");
    setErrors({});
  }, [procedure, action]);

  const asksDecision = !!action?.fields.some((f) => f.kind === "decision");
  useEffect(() => {
    if (!asksDecision || decisions.length > 0) return;
    let active = true;
    permitsApi
      .getProcedureDecisionOptions()
      .then((options) => active && setDecisions(options))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [asksDecision, decisions.length]);

  const targetOptions = useMemo(
    () =>
      (action?.target_options ?? []).map((status) => ({
        value: status,
        label:
          procedure?.status_steps.find((step) => step.status === status)
            ?.label ?? status,
      })),
    [action, procedure],
  );

  if (!procedure || !action) return null;

  /** Setzt ein Feld und lässt die Felder mitlaufen, die von ihm aus vorgeschlagen sind. */
  const set = (name: string, value: Value) =>
    setValues((prev) => {
      const next: Values = { ...prev, [name]: value };
      action.fields.forEach((field) => {
        if (field.suggest_after?.base !== name || field.name === name) return;
        const previous = suggestion(field, (prev[name] as string) ?? "");
        if (!prev[field.name] || prev[field.name] === previous) {
          next[field.name] = suggestion(field, value as string) ?? "";
        }
      });
      return next;
    });

  const check = (): boolean => {
    const found: Record<string, string> = {};
    action.fields.forEach((field) => {
      if (field.required && isEmpty(values[field.name])) {
        found[field.name] = "Pflichtfeld";
      }
    });
    if (action.comment_required && !comment.trim()) {
      found.comment = "Pflichtfeld";
    }
    if (action.target_options.length > 0 && !target) {
      found.target = "Pflichtfeld";
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async () => {
    if (!check()) return;
    setRunning(true);
    try {
      const fields = Object.fromEntries(
        action.fields.map((field) => {
          const value = values[field.name];
          if (Array.isArray(value)) {
            const documents = value.filter((document) => document.url.trim());
            return [field.name, documents.length > 0 ? documents : null];
          }
          return [field.name, value?.trim() || null];
        }),
      );
      const ok = await onSubmit({
        action: action.action,
        fields,
        comment: comment.trim() || null,
        target_status: target || null,
      });
      if (ok) onClose();
    } finally {
      setRunning(false);
    }
  };

  return (
    <DialogTemplate
      open
      onClose={onClose}
      title={action.label}
      maxWidth="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={running}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={running}>
            {action.label}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        {action.target_options.length > 0 && (
          <Dropdown
            label="Zurück auf"
            value={target}
            options={targetOptions}
            onChange={(value) => setTarget(value as ProcedureStatus)}
            variant="floating"
            size="small"
            error={!!errors.target}
            helperText={errors.target}
          />
        )}

        {action.fields.map((field) =>
          field.kind === "documents" ? (
            <DokumentListe
              key={field.name}
              label={field.label}
              dokumente={(values[field.name] as ProcedureDocument[]) ?? []}
              onChange={(list) => set(field.name, list)}
              required={field.required}
              error={errors[field.name]}
            />
          ) : field.kind === "decision" ? (
            <Dropdown
              key={field.name}
              label={field.label}
              value={(values[field.name] as string) ?? ""}
              options={decisions.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              onChange={(value) => set(field.name, value)}
              variant="floating"
              size="small"
              error={!!errors[field.name]}
              helperText={errors[field.name]}
            />
          ) : (
            <InputFieldDate
              key={field.name}
              label={field.label}
              value={(values[field.name] as string) ?? ""}
              onChange={(value) => set(field.name, value ?? "")}
              required={field.required}
              error={errors[field.name]}
              variant="floating"
              size="small"
              dateFormat="dd.MM.yyyy"
            />
          ),
        )}

        <InputFieldMultiline
          label={action.comment_required ? "Kommentar" : "Kommentar (optional)"}
          value={comment}
          onChange={setComment}
          required={action.comment_required}
          error={errors.comment}
          minRows={3}
        />
      </Box>
    </DialogTemplate>
  );
};

export default AntragUebergangDialog;
